import React, { useEffect, useState } from 'react';
import { Box, Typography, styled, useTheme, CircularProgress } from '@mui/material';
import { QueryVariantWithClinvar, GeneStructure } from '../../types/genomic';
import { ClinvarVariantModifier } from "@gen/lda/v1alpha1/genomicquery_pb";
import { EnsemblService } from '../../services/EnsemblService';

const GeneContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  pageBreakInside: 'avoid',
}));


interface Props {
  gene: string;
  variants?: QueryVariantWithClinvar[];
}

export const GeneVisualization: React.FC<Props> = ({ gene, variants = [] }) => {
  const theme = useTheme();
  const [geneStructure, setGeneStructure] = useState<GeneStructure | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGeneData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await EnsemblService.fetchGeneStructure(gene);
        if (data) {
          setGeneStructure(data);
        }
      } catch (err) {
        setError('Failed to load gene structure. Please try again later.');
        console.error('Error loading gene structure:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGeneData();
  }, [gene]);

  if (loading) {
    return (
      <GeneContainer>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      </GeneContainer>
    );
  }

  if (error || !geneStructure) {
    return (
      <></>
    );
  }

  const geneLength = geneStructure.end - geneStructure.start;
  const svgWidth = 800;
  const svgHeight = 120;
  const exonHeight = 20;
  const intronHeight = 4;
  const arrowSize = 6;
  const labelOffset = 25;

  const calculateXPosition = (position: number): number => {
    return ((position - geneStructure.start) / geneLength) * svgWidth;
  };

  const getVariantColor = (variant: QueryVariantWithClinvar) => {
    const clinvarModifier: ClinvarVariantModifier = variant.variantModifier.find(modifier => modifier.modifier.case === "clinvar")?.modifier.value as ClinvarVariantModifier;
    if (clinvarModifier.pathogenicity === 'Pathogenic') {
      return theme.palette.error.main;
    } else if (clinvarModifier.pathogenicity === 'Likely Pathogenic') {
      return theme.palette.error.main;
    } else if (clinvarModifier.pathogenicity === 'Drug Response') {
      return theme.palette.info.main;
    } else if (clinvarModifier.pathogenicity === 'Benign') {
      return theme.palette.success.main;
    } else {
      return theme.palette.warning.main;
    }
  };

  // Add marker size constants
  const markerSize = 8;
  const markerStrokeWidth = 1.5;

  // Determine which end is 5' based on strand
  const [fivePrimeX, threePrimeX] = geneStructure.strand === '+' ? [0, svgWidth] : [svgWidth, 0];

  return (
    <GeneContainer>
      <svg width={"100%"} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
        {/* Gene body (introns) */}
        <line
          x1={0}
          y1={svgHeight / 2 + labelOffset}
          x2={svgWidth}
          y2={svgHeight / 2 + labelOffset}
          stroke={theme.palette.grey[300]}
          strokeWidth={intronHeight}
        />

        {/* Reading direction text */}
        <text
          x={svgWidth / 2}
          y={25}
          textAnchor="middle"
          fill={theme.palette.text.secondary}
          style={{ fontSize: '12px' }}
        >
          Reading direction: {geneStructure.strand === '+' ? '5\' → 3\'' : '3\' ← 5\''}
        </text>

        {/* 5' and 3' labels */}
        <text
          x={fivePrimeX + (geneStructure.strand === '+' ? 5 : -5)}
          y={svgHeight / 2 + labelOffset - 25}
          textAnchor={geneStructure.strand === '+' ? 'start' : 'end'}
          fill={theme.palette.text.secondary}
          style={{ fontSize: '12px', fontWeight: 'bold' }}
        >
          5'
        </text>
        <text
          x={threePrimeX + (geneStructure.strand === '+' ? -5 : 5)}
          y={svgHeight / 2 + labelOffset - 25}
          textAnchor={geneStructure.strand === '+' ? 'end' : 'start'}
          fill={theme.palette.text.secondary}
          style={{ fontSize: '12px', fontWeight: 'bold' }}
        >
          3'
        </text>
        
        {/* Exons */}
        {geneStructure.exons.map((exon, index) => {
          const x = calculateXPosition(exon.start);
          const width = calculateXPosition(exon.end) - x;
          const exonNumber = geneStructure.strand === '+' ? index + 1 : geneStructure.exons.length - index;
          
          return (
            <g key={`exon-${index}`}>
              <rect
                x={x}
                y={(svgHeight - exonHeight) / 2 + labelOffset}
                width={width}
                height={exonHeight}
                fill={theme.palette.primary.main}
                opacity={0.5}
                strokeWidth={1}
              >
                <title>{`Exon ${exonNumber}\nPosition: ${exon.start.toLocaleString()}-${exon.end.toLocaleString()}`}</title>
              </rect>
              <text
                x={x + width / 2}
                y={svgHeight - 5}
                textAnchor="middle"
                fill={theme.palette.text.secondary}
                style={{ fontSize: '8px' }}
              >
                {exonNumber}
              </text>
            </g>
          );
        })}

        {/* Variant markers */}
        {variants.map((variant, index) => {
          const position = Number(variant.position);
          if (position >= geneStructure.start && position <= geneStructure.end) {
            const x = calculateXPosition(position);
            const color = getVariantColor(variant);
            const clinvarModifier = variant.variantModifier.find(m => m.modifier.case === "clinvar")?.modifier.value as ClinvarVariantModifier;
            
            return (
              <g className="variant-marker" key={`variant-${index}`}>
                {/* Subtle glow effect */}
                <circle
                  cx={x}
                  cy={svgHeight / 2 + labelOffset}
                  r={markerSize + 2}
                  fill={color}
                  opacity={0.2}
                  filter="url(#glow)"
                />
                
                {/* Diamond shape marker */}
                <path
                  d={`M ${x} ${svgHeight / 2 + labelOffset - markerSize}
                     L ${x + markerSize} ${svgHeight / 2 + labelOffset}
                     L ${x} ${svgHeight / 2 + labelOffset + markerSize}
                     L ${x - markerSize} ${svgHeight / 2 + labelOffset} Z`}
                  fill={color}
                  stroke={theme.palette.background.paper}
                  strokeWidth={markerStrokeWidth}
                  opacity={0.9}
                >
                  <title>
                    Position: {position.toLocaleString()}
                    {clinvarModifier?.pathogenicity ? `\nClinical Significance: ${clinvarModifier.pathogenicity}` : ''}
                    {variant.reference && variant.alternate ? `\nVariant: ${variant.reference}>${variant.alternate}` : ''}
                  </title>
                </path>
                
                {/* Connecting line */}
                <line
                  x1={x}
                  y1={svgHeight / 2 + labelOffset - markerSize - 5}
                  x2={x}
                  y2={svgHeight / 2 + labelOffset + markerSize + 5}
                  stroke={color}
                  strokeWidth={1}
                  opacity={0.3}
                />
              </g>
            );
          }
          return null;
        })}

        {/* Add filter definition for glow effect */}
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Reading direction arrow - moved to end so it renders on top */}
        {geneStructure.strand === '+' ? (
          <path
            d={`M ${svgWidth - 20} ${svgHeight / 2 + labelOffset} l ${arrowSize} ${-arrowSize} l 0 ${2 * arrowSize} Z`}
            fill={theme.palette.text.secondary}
          />
        ) : (
          <path
            d={`M 20 ${svgHeight / 2 + labelOffset} l ${-arrowSize} ${-arrowSize} l 0 ${2 * arrowSize} Z`}
            fill={theme.palette.text.secondary}
          />
        )}
      </svg>
      
      {/* Legend */}
      <Box mt={1} display="flex" gap={2} alignItems="center">
        <Box display="flex" alignItems="center">
          <Box
            width={20}
            height={exonHeight}
            bgcolor="primary.main"
            mr={0.5}
            border={1}
            borderColor="primary.dark"
          />
          <Typography variant="caption">Exon</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Box
            width={20}
            height={intronHeight}
            bgcolor="grey.300"
            mr={0.5}
          />
          <Typography variant="caption">Intron</Typography>
        </Box>
      </Box>
    </GeneContainer>
  );
}; 